import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import { Alert, Button, Input, Modal, Result, Select, Space, Switch, Table, Tabs, Tag, Typography, notification } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, FormOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';

const { Title, Paragraph, Text } = Typography;
const PERMS = ['form_config.manage', 'admin.*'];
const FORMS = [
  { key: 'registration', label: 'Member sign-up (app)', note: 'The self-service registration in the member app.' },
  { key: 'org_client', label: 'Add client to organization', note: 'Organizations → members → Add, in this portal.' },
  { key: 'enrollee', label: 'Enrollee / dependent', note: 'Adding a principal or dependent, in both apps.' },
];

function ReferenceLists() {
  const [lists, setLists] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newList, setNewList] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await lib.formConfig.lists();
    if (res?.error) notification.error({ message: res.message, placement: 'topLeft' });
    const rows = res?.data?.lists || [];
    setLists(rows);
    setActive((a) => a || rows[0]?.list_key || null);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => lists.find((l) => l.list_key === active) || null, [lists, active]);

  const add = async () => {
    if (!newLabel.trim()) return;
    const res = await lib.formConfig.addOption(active, { label: newLabel.trim(), code: newCode.trim() });
    if (res?.error) { notification.error({ message: res.message, placement: 'topLeft' }); return; }
    setNewLabel(''); setNewCode('');
    load();
  };
  const patch = async (opt, body) => {
    const res = await lib.formConfig.updateOption(opt.id, body);
    if (res?.error) { notification.error({ message: res.message, placement: 'topLeft' }); return; }
    load();
  };
  const move = async (index, dir) => {
    const ids = list.options.map((o) => o.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    const res = await lib.formConfig.reorderOptions(active, ids);
    if (res?.error) { notification.error({ message: res.message, placement: 'topLeft' }); return; }
    load();
  };
  const createList = async () => {
    const res = await lib.formConfig.createList(newList);
    if (res?.error) { notification.error({ message: res.message, placement: 'topLeft' }); return; }
    setNewList(null); setActive(res.data.list_key); load();
  };

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Option lists the forms offer"
        description="Add or rename options, hide ones that should no longer be chosen, and set their order. The stored value never changes once an option exists, so past records keep matching. A hidden option stays on existing records." />
      <Space style={{ marginBottom: 12 }} wrap>
        <Select style={{ width: 280 }} value={active} onChange={setActive} options={lists.map((l) => ({ value: l.list_key, label: `${l.name} (${l.options.length})` }))} />
        <Button icon={<PlusOutlined />} onClick={() => setNewList({ list_key: '', name: '', description: '' })}>New list</Button>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>
        {list?.description ? <Text type="secondary">{list.description}</Text> : null}
      </Space>
      {list ? (
        <>
          <Table size="small" rowKey="id" pagination={false} dataSource={list.options} loading={loading}
            columns={[
              { title: '#', key: 'n', width: 50, render: (_, __, i) => i + 1 },
              { title: 'Label (what people see)', key: 'label', render: (_, o) => (
                <Input size="small" defaultValue={o.label} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== o.label) patch(o, { label: e.target.value.trim() }); }} />
              ) },
              { title: 'Stored value', key: 'code', width: 180, render: (_, o) => <Text code>{o.code}</Text> },
              { title: 'Offered', key: 'status', width: 100, render: (_, o) => <Switch size="small" checked={Number(o.status) === 1} onChange={(v) => patch(o, { status: v ? 1 : 0 })} /> },
              { title: 'Order', key: 'order', width: 100, render: (_, __, i) => (
                <Space size={2}>
                  <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
                  <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === list.options.length - 1} onClick={() => move(i, 1)} />
                </Space>
              ) },
            ]} />
          <Space style={{ marginTop: 12 }} wrap>
            <Input placeholder="New option label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} style={{ width: 240 }} onPressEnter={add} />
            <Input placeholder="Stored value (optional)" value={newCode} onChange={(e) => setNewCode(e.target.value)} style={{ width: 200 }} onPressEnter={add} />
            <Button type="primary" icon={<PlusOutlined />} onClick={add} disabled={!newLabel.trim()}>Add option</Button>
          </Space>
        </>
      ) : null}
      <Modal open={!!newList} title="New reference list" onCancel={() => setNewList(null)} onOk={createList} okText="Create" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input placeholder="Key (e.g. id_type) — letters, digits, underscore" value={newList?.list_key || ''} onChange={(e) => setNewList((l) => ({ ...l, list_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} />
          <Input placeholder="Name (e.g. ID type)" value={newList?.name || ''} onChange={(e) => setNewList((l) => ({ ...l, name: e.target.value }))} />
          <Input placeholder="Description (optional)" value={newList?.description || ''} onChange={(e) => setNewList((l) => ({ ...l, description: e.target.value }))} />
          <Text type="secondary" style={{ fontSize: 12 }}>A new list only appears on a form once a select field on that form is pointed at it (Form fields tab).</Text>
        </Space>
      </Modal>
    </div>
  );
}

function FormFields() {
  const [formKey, setFormKey] = useState(FORMS[0].key);
  const [fields, setFields] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await lib.formConfig.fields(formKey);
    if (res?.error) notification.error({ message: res.message, placement: 'topLeft' });
    setFields(res?.data?.fields || []);
    setLists(res?.data?.lists || []);
    setLoading(false);
  }, [formKey]);
  useEffect(() => { load(); }, [load]);

  const patch = async (f, body) => {
    const res = await lib.formConfig.updateField(f.id, body);
    if (res?.error) { notification.error({ message: res.message, placement: 'topLeft', duration: 6 }); return; }
    load();
  };
  const move = async (index, dir) => {
    const ids = fields.map((f) => f.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    const res = await lib.formConfig.reorderFields(formKey, ids);
    if (res?.error) { notification.error({ message: res.message, placement: 'topLeft' }); return; }
    load();
  };
  const form = FORMS.find((f) => f.key === formKey);

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Configure the fields each form shows"
        description="Every field here is known to the system. You decide whether it shows, whether it is required, what it is called, its help text, its order, and which list feeds a dropdown. Fields marked locked are needed by the registration pipeline and always show as required." />
      <Space style={{ marginBottom: 12 }} wrap>
        <Select style={{ width: 300 }} value={formKey} onChange={setFormKey} options={FORMS.map((f) => ({ value: f.key, label: f.label }))} />
        <Text type="secondary">{form?.note}</Text>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>
      </Space>
      <Table size="small" rowKey="id" pagination={false} dataSource={fields} loading={loading}
        columns={[
          { title: 'Field', key: 'k', width: 170, render: (_, f) => (<div><Text code>{f.field_key}</Text>{f.locked ? <Tag style={{ marginInlineStart: 6 }}>locked</Tag> : null}<div style={{ fontSize: 11, color: '#888' }}>{f.input_type}</div></div>) },
          { title: 'Label', key: 'label', render: (_, f) => <Input size="small" defaultValue={f.label} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== f.label) patch(f, { label: e.target.value.trim() }); }} /> },
          { title: 'Help text', key: 'help', render: (_, f) => <Input size="small" defaultValue={f.help_text || ''} placeholder="—" onBlur={(e) => { if ((e.target.value || '') !== (f.help_text || '')) patch(f, { help_text: e.target.value }); }} /> },
          { title: 'Options from', key: 'list', width: 190, render: (_, f) => f.input_type === 'select' ? (
            <Select size="small" style={{ width: 170 }} value={f.list_key || undefined} placeholder="(built-in)" allowClear onChange={(v) => patch(f, { list_key: v || '' })} options={lists.map((l) => ({ value: l.list_key, label: l.name }))} />
          ) : <Text type="secondary">—</Text> },
          { title: 'Shows', key: 'visible', width: 80, render: (_, f) => <Switch size="small" checked={!!f.visible} disabled={f.locked} onChange={(v) => patch(f, { visible: v ? 1 : 0 })} /> },
          { title: 'Required', key: 'required', width: 90, render: (_, f) => <Switch size="small" checked={!!f.required} disabled={f.locked} onChange={(v) => patch(f, { required: v ? 1 : 0 })} /> },
          { title: 'Order', key: 'order', width: 100, render: (_, __, i) => (
            <Space size={2}>
              <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
              <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === fields.length - 1} onClick={() => move(i, 1)} />
            </Space>
          ) },
        ]} />
    </div>
  );
}

export default function FormSettingsPage() {
  const { hasAnyPermission } = usePermissions();
  if (!hasAnyPermission(PERMS)) {
    return <Result status="403" title="Not available" subTitle="Form settings require the form_config.manage permission." />;
  }
  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginTop: 0 }}><FormOutlined /> Form settings</Title>
      <ModuleNav items={MODULE_NAVS.enrollees} />
      <Paragraph type="secondary">Reference lists (states, gender, relationship, titles and more) and the settings of each registration form. Changes apply to the apps within five minutes; nothing here needs a release.</Paragraph>
      <Tabs items={[
        { key: 'lists', label: 'Reference lists', children: <ReferenceLists /> },
        { key: 'fields', label: 'Form fields', children: <FormFields /> },
      ]} />
    </div>
  );
}
